import React, { useState, useEffect, useRef } from "react";
import { Save, Upload, Plus, Trash2, GripVertical, ExternalLink } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";
import { useSettings } from "../../context/SettingsContext";

const SOCIAL_PLATFORMS = [
  { value: "tiktok", label: "TikTok" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "youtube", label: "YouTube" },
  { value: "spotify", label: "Spotify" },
  { value: "soundcloud", label: "SoundCloud" },
  { value: "twitter", label: "X / Twitter" },
  { value: "twitch", label: "Twitch" },
  { value: "website", label: "Website" },
];

const LANG_TABS = [
  { code: "de", label: "DE" },
  { code: "en", label: "EN" },
  { code: "pl", label: "PL" },
];

/** Converts any value (string | object | null) to a {de,en,pl} shape */
function toLocalized(val) {
  if (!val) return { de: "", en: "", pl: "" };
  if (typeof val === "string") return { de: val, en: "", pl: "" };
  return { de: val.de || "", en: val.en || "", pl: val.pl || "" };
}

/** Language-tab input for translatable text fields */
function LocalizedInput({ label, value, onChange, multiline = false, inputCls, labelCls }) {
  const [activeLang, setActiveLang] = useState("de");
  const val = typeof value === "object" && value !== null ? value : { de: value || "", en: "", pl: "" };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className={labelCls}>{label}</label>
        <div className="flex gap-0.5 bg-zinc-800 rounded-lg p-0.5">
          {LANG_TABS.map(l => (
            <button
              key={l.code}
              type="button"
              onClick={() => setActiveLang(l.code)}
              className={`px-2.5 py-0.5 rounded text-xs font-bold transition-all ${
                activeLang === l.code
                  ? "bg-rose-600 text-white"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      {multiline ? (
        <textarea
          value={val[activeLang] || ""}
          onChange={e => onChange({ ...val, [activeLang]: e.target.value })}
          rows={2}
          className={`${inputCls} resize-none`}
          placeholder={activeLang !== "de" && val.de ? `Fallback (DE): ${val.de}` : ""}
        />
      ) : (
        <input
          value={val[activeLang] || ""}
          onChange={e => onChange({ ...val, [activeLang]: e.target.value })}
          className={inputCls}
          placeholder={activeLang !== "de" && val.de ? `Fallback (DE): ${val.de}` : ""}
        />
      )}
    </div>
  );
}

export default function SiteSettingsPage() {
  const { refresh } = useSettings();
  const [form, setForm] = useState({
    site_name: "", site_description: "", logo_url: "", favicon_url: "",
    hero_image_url: "", hero_mobile_image_url: "",
    hero_title: { de: "", en: "", pl: "" },
    hero_subtitle: { de: "", en: "", pl: "" },
    footer_text: { de: "", en: "", pl: "" },
    social_links: [], partners: [], footer_links: [], nav_links: [],
    affiliate_logos: [{ image_url: "", link_url: "", title: "" }, { image_url: "", link_url: "", title: "" }],
    google_analytics_id: "", cookie_banner_code: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const logoRef = useRef(null);
  const heroRef       = useRef(null);
  const heroMobileRef = useRef(null);
  const affiliateRef0 = useRef(null);
  const affiliateRef1 = useRef(null);
  const affiliateRefs = [affiliateRef0, affiliateRef1];

  useEffect(() => {
    api.get("/settings/").then((res) => setForm({
      site_name: res.data.site_name || "",
      site_description: res.data.site_description || "",
      logo_url: res.data.logo_url || "",
      favicon_url: res.data.favicon_url || "",
      hero_image_url: res.data.hero_image_url || "",
      hero_mobile_image_url: res.data.hero_mobile_image_url || "",
      hero_title: toLocalized(res.data.hero_title),
      hero_subtitle: toLocalized(res.data.hero_subtitle),
      footer_text: toLocalized(res.data.footer_text),
      social_links: res.data.social_links || [],
      partners: res.data.partners || [],
      footer_links: res.data.footer_links || [],
      nav_links: res.data.nav_links || [],
      affiliate_logos: (() => {
        const raw = res.data.affiliate_logos || [];
        // always exactly 2 slots
        return [
          raw[0] ? { image_url: raw[0].image_url || "", link_url: raw[0].link_url || "", title: raw[0].title || "" } : { image_url: "", link_url: "", title: "" },
          raw[1] ? { image_url: raw[1].image_url || "", link_url: raw[1].link_url || "", title: raw[1].title || "" } : { image_url: "", link_url: "", title: "" },
        ];
      })(),
      google_analytics_id: res.data.google_analytics_id || "",
      cookie_banner_code: res.data.cookie_banner_code || "",
    })).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setLocalized = (field) => (val) => setForm((f) => ({ ...f, [field]: val }));

  const handleUpload = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/upload/image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: res.data.url }));
    } catch { alert("Upload fehlgeschlagen"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);
    try {
      await api.put("/settings/", form);
      setSuccess(true);
      refresh();
      setTimeout(() => setSuccess(false), 3000);
    } catch { alert("Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  // Social links helpers
  const addSocialLink = () => setForm(f => ({ ...f, social_links: [...f.social_links, { platform: "instagram", url: "", label: "" }] }));
  const updateSocialLink = (i, key, val) => setForm(f => {
    const links = [...f.social_links];
    links[i] = { ...links[i], [key]: val };
    return { ...f, social_links: links };
  });
  const removeSocialLink = (i) => setForm(f => ({ ...f, social_links: f.social_links.filter((_, idx) => idx !== i) }));

  // Partner helpers
  const addPartner = () => setForm(f => ({ ...f, partners: [...f.partners, { name: "", logo_url: "", link_url: "", sort_order: f.partners.length }] }));
  const updatePartner = (i, key, val) => setForm(f => {
    const p = [...f.partners];
    p[i] = { ...p[i], [key]: val };
    return { ...f, partners: p };
  });
  const removePartner = (i) => setForm(f => ({ ...f, partners: f.partners.filter((_, idx) => idx !== i) }));

  const addFooterLink = (group) => setForm(f => ({ ...f, footer_links: [...f.footer_links, { label: "", url: "", is_external: false, group }] }));
  const updateFooterLink = (i, key, val) => setForm(f => {
    const links = [...f.footer_links]; links[i] = { ...links[i], [key]: val }; return { ...f, footer_links: links };
  });
  const removeFooterLink = (i) => setForm(f => ({ ...f, footer_links: f.footer_links.filter((_, idx) => idx !== i) }));

  const addNavLink = () => setForm(f => ({ ...f, nav_links: [...f.nav_links, { label: "", url: "", is_external: false }] }));
  const updateNavLink = (i, key, val) => setForm(f => {
    const links = [...f.nav_links]; links[i] = { ...links[i], [key]: val }; return { ...f, nav_links: links };
  });
  const removeNavLink = (i) => setForm(f => ({ ...f, nav_links: f.nav_links.filter((_, idx) => idx !== i) }));
  const uploadPartnerLogo = (i) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/upload/image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      updatePartner(i, "logo_url", res.data.url);
    } catch { alert("Upload fehlgeschlagen"); }
  };

  // Affiliate logo helpers (exactly 2 slots)
  const updateAffiliate = (i, key, val) => setForm(f => {
    const logos = [...f.affiliate_logos];
    logos[i] = { ...logos[i], [key]: val };
    return { ...f, affiliate_logos: logos };
  });
  const uploadAffiliateLogo = (i) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateAffiliate(i, "image_url", res.data.url);
    } catch { alert("Upload fehlgeschlagen"); }
  };

  const inputCls = "w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600";
  // Ohne w-full – für Inputs innerhalb von Flex/Grid-Rows
  const inputBase = "bg-zinc-900 border border-zinc-800 text-white rounded-lg px-3 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600";
  const labelCls = "block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-1.5";

  if (loading) return <AdminLayout><div className="p-8"><div className="h-8 animate-pulse bg-zinc-800 rounded w-48" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-white font-heading">Site-Einstellungen</h1>
            <p className="text-zinc-500 text-sm mt-0.5">Nur für Developer sichtbar</p>
          </div>
          {success && <span className="text-green-400 text-sm font-semibold flex items-center gap-1"><Save size={14} /> Gespeichert!</span>}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Allgemein */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-4">
            <h2 className="text-white font-bold text-sm">Allgemein</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Site-Name</label>
                <input value={form.site_name} onChange={set("site_name")} className={inputCls} placeholder="HUWND" />
              </div>
              <div>
                <label className={labelCls}>Beschreibung</label>
                <input value={form.site_description} onChange={set("site_description")} className={inputCls} />
              </div>
            </div>
            <LocalizedInput
              label="Footer-Text"
              value={form.footer_text}
              onChange={setLocalized("footer_text")}
              multiline
              inputCls={`${inputCls} w-full`}
              labelCls={labelCls}
            />
          </div>

          {/* Logo */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-4">
            <h2 className="text-white font-bold text-sm">Logo &amp; Favicon</h2>
            <div>
              <label className={labelCls}>Logo URL</label>
              <div className="flex gap-2">
                <input value={form.logo_url} onChange={set("logo_url")} className={`${inputCls} flex-1`} placeholder="https://..." data-testid="settings-logo-url" />
                <button type="button" onClick={() => logoRef.current?.click()} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors">
                  <Upload size={14} />
                </button>
              </div>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleUpload("logo_url")} />
              {form.logo_url && <img src={form.logo_url} alt="Logo" className="mt-2 h-12 object-contain rounded bg-zinc-800 p-1 border border-white/5" />}
            </div>
            <div>
              <label className={labelCls}>Favicon URL</label>
              <input value={form.favicon_url} onChange={set("favicon_url")} className={inputCls} placeholder="https://..." />
            </div>
          </div>

          {/* Hero */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-4">
            <h2 className="text-white font-bold text-sm">Hero-Bereich</h2>
            <div>
              <label className={labelCls}>Hero-Bild Desktop</label>
              <div className="flex gap-2">
                <input value={form.hero_image_url} onChange={set("hero_image_url")} className={`${inputCls} flex-1`} data-testid="settings-hero-url" />
                <button type="button" onClick={() => heroRef.current?.click()} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors">
                  <Upload size={14} />
                </button>
              </div>
              <input ref={heroRef} type="file" accept="image/*" className="hidden" onChange={handleUpload("hero_image_url")} />
              {form.hero_image_url && (
                <div className="mt-2 h-20 rounded-lg overflow-hidden">
                  <img src={form.hero_image_url} alt="Hero Desktop Preview" className="w-full h-full object-cover opacity-70" />
                </div>
              )}
            </div>
            <div>
              <label className={labelCls}>Hero-Bild Mobile <span className="text-zinc-600 normal-case font-normal">(optional – wird nur auf Mobilgeräten angezeigt)</span></label>
              <div className="flex gap-2">
                <input value={form.hero_mobile_image_url} onChange={set("hero_mobile_image_url")} className={`${inputCls} flex-1`} placeholder="Leer = Desktop-Bild wird verwendet" />
                <button type="button" onClick={() => heroMobileRef.current?.click()} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors">
                  <Upload size={14} />
                </button>
              </div>
              <input ref={heroMobileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload("hero_mobile_image_url")} />
              {form.hero_mobile_image_url && (
                <div className="mt-2 h-20 rounded-lg overflow-hidden">
                  <img src={form.hero_mobile_image_url} alt="Hero Mobile Preview" className="w-full h-full object-cover opacity-70" />
                </div>
              )}
            </div>
            <LocalizedInput
              label="Hero-Titel"
              value={form.hero_title}
              onChange={setLocalized("hero_title")}
              inputCls={inputCls}
              labelCls={labelCls}
            />
            <LocalizedInput
              label="Hero-Untertitel"
              value={form.hero_subtitle}
              onChange={setLocalized("hero_subtitle")}
              multiline
              inputCls={`${inputCls} w-full`}
              labelCls={labelCls}
            />
          </div>

          {/* Social Links */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-bold text-sm">Social Media Links</h2>
              <button type="button" onClick={addSocialLink}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold rounded-lg transition-colors">
                <Plus size={12} /> Link hinzufügen
              </button>
            </div>
            {form.social_links.length === 0 && (
              <p className="text-zinc-600 text-xs">Noch keine Social Links. Klicke auf „Link hinzufügen".</p>
            )}
            {form.social_links.map((link, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={link.platform} onChange={(e) => updateSocialLink(i, "platform", e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 text-white rounded-lg px-2 py-2 text-xs outline-none w-32 flex-shrink-0">
                  {SOCIAL_PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <input value={link.url} onChange={(e) => updateSocialLink(i, "url", e.target.value)}
                  placeholder="https://..." className={`${inputCls} flex-1 py-2 text-xs`} />
                <button type="button" onClick={() => removeSocialLink(i)}
                  className="p-2 text-zinc-600 hover:text-red-400 transition-colors flex-shrink-0">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Partners */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-sm">Partner</h2>
                <p className="text-zinc-600 text-xs mt-0.5">Werden auf der Startseite angezeigt</p>
              </div>
              <button type="button" onClick={addPartner}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold rounded-lg transition-colors">
                <Plus size={12} /> Partner hinzufügen
              </button>
            </div>
            {form.partners.length === 0 && (
              <p className="text-zinc-600 text-xs">Noch keine Partner eingetragen.</p>
            )}
            {form.partners.map((p, i) => {
              const fileRef = React.createRef();
              return (
                <div key={i} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-xl border border-white/5">
                  {/* Logo */}
                  <div className="flex-shrink-0">
                    <div
                      className="w-14 h-14 rounded-lg bg-zinc-900 border border-white/8 overflow-hidden flex items-center justify-center cursor-pointer hover:border-rose-500/40 transition-colors"
                      onClick={() => fileRef.current?.click()}
                    >
                      {p.logo_url ? (
                        <img src={p.logo_url} alt={p.name} className="w-full h-full object-contain p-1" />
                      ) : (
                        <Upload size={16} className="text-zinc-600" />
                      )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPartnerLogo(i)} />
                  </div>
                  {/* Fields */}
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input value={p.name} onChange={(e) => updatePartner(i, "name", e.target.value)}
                      placeholder="Partnername" className={`${inputCls} py-2 text-xs`} />
                    <input value={p.link_url || ""} onChange={(e) => updatePartner(i, "link_url", e.target.value)}
                      placeholder="https://..." className={`${inputCls} py-2 text-xs`} />
                  </div>
                  <button type="button" onClick={() => removePartner(i)}
                    className="p-2 text-zinc-600 hover:text-red-400 transition-colors flex-shrink-0 mt-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Hauptmenü Links */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-sm">Hauptmenü Links</h2>
                <p className="text-zinc-500 text-xs mt-0.5">Erscheinen im Header nach den festen Menüpunkten. Intern (/search) oder extern (https://...).</p>
              </div>
              <button type="button" onClick={addNavLink}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold rounded-lg transition-colors">
                <Plus size={12} /> Link hinzufügen
              </button>
            </div>
            {form.nav_links.length === 0 && (
              <p className="text-zinc-600 text-xs">Noch keine Links. Klicke auf „Link hinzufügen".</p>
            )}
            {form.nav_links.map((link, i) => (
              <div key={i} className="grid items-center gap-2" style={{ gridTemplateColumns: "7rem 1fr auto auto" }}>
                <input
                  value={link.label}
                  onChange={e => updateNavLink(i, "label", e.target.value)}
                  placeholder="Bezeichnung"
                  className={`${inputBase} py-1.5 text-xs`}
                />
                <input
                  value={link.url}
                  onChange={e => updateNavLink(i, "url", e.target.value)}
                  placeholder="/seite oder https://..."
                  className={`${inputBase} py-1.5 text-xs min-w-0`}
                />
                <label className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-xs cursor-pointer whitespace-nowrap select-none">
                  <input
                    type="checkbox"
                    checked={link.is_external}
                    onChange={e => updateNavLink(i, "is_external", e.target.checked)}
                    className="accent-rose-600 w-3.5 h-3.5 flex-shrink-0"
                  />
                  Extern
                </label>
                <button type="button" onClick={() => removeNavLink(i)}
                  className="text-zinc-600 hover:text-red-400 transition-colors p-1">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>

          {/* Footer Navigation Links */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-4">
            <h2 className="text-white font-bold text-sm">Footer-Navigation</h2>
            <p className="text-zinc-500 text-xs">Interne Seiten (/search) oder externe Links (https://...). Externe Links öffnen in neuem Tab.</p>

            {["nav", "legal"].map(group => (
              <div key={group}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">
                    {group === "nav" ? "Navigation" : "Rechtliches"}
                  </p>
                  <button type="button" onClick={() => addFooterLink(group)}
                    className="text-xs px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded transition-colors flex items-center gap-1">
                    <Plus size={11} /> Link
                  </button>
                </div>
                <div className="space-y-2">
                  {form.footer_links
                    .map((link, idx) => ({ link, idx }))
                    .filter(({ link }) => link.group === group)
                    .map(({ link, idx }) => (
                      <div key={idx} className="grid items-center gap-2" style={{ gridTemplateColumns: "7rem 1fr auto auto" }}>
                        <input
                          value={link.label}
                          onChange={e => updateFooterLink(idx, "label", e.target.value)}
                          placeholder="Bezeichnung"
                          className={`${inputBase} py-1.5 text-xs`}
                        />
                        <input
                          value={link.url}
                          onChange={e => updateFooterLink(idx, "url", e.target.value)}
                          placeholder="/search oder https://..."
                          className={`${inputBase} py-1.5 text-xs min-w-0`}
                        />
                        <label className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-xs cursor-pointer whitespace-nowrap select-none">
                          <input
                            type="checkbox"
                            checked={link.is_external}
                            onChange={e => updateFooterLink(idx, "is_external", e.target.checked)}
                            className="accent-rose-600 w-3.5 h-3.5 flex-shrink-0"
                          />
                          Extern
                        </label>
                        <button type="button" onClick={() => removeFooterLink(idx)}
                          className="text-zinc-600 hover:text-red-400 transition-colors p-1">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  {form.footer_links.filter(l => l.group === group).length === 0 && (
                    <p className="text-zinc-700 text-xs">Keine Links – „+ Link" klicken.</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Affiliate-Links */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-4">
            <div>
              <h2 className="text-white font-bold text-sm">Affiliate-Links</h2>
              <p className="text-zinc-500 text-xs mt-0.5">2 Partner-Logos mit Links für den Footer (z.B. Hoster, e-recht24).</p>
            </div>
            {form.affiliate_logos.map((logo, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg border border-white/[0.04]">
                {/* Preview */}
                <div className="w-14 h-14 rounded-lg bg-zinc-800 border border-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {logo.image_url
                    ? <img src={logo.image_url} alt="" className="w-full h-full object-contain p-1" onError={(e) => { e.target.style.display = "none"; }} />
                    : <span className="text-zinc-600 text-xs font-bold">Logo {i + 1}</span>}
                </div>
                {/* Fields */}
                <div className="flex-1 grid grid-cols-1 gap-2">
                  <div className="flex gap-2">
                    <input
                      value={logo.image_url || ""}
                      onChange={(e) => updateAffiliate(i, "image_url", e.target.value)}
                      placeholder="Logo-URL (https://...)"
                      data-testid={`affiliate-logo-url-${i}`}
                      className={`${inputCls} py-2 text-xs flex-1`}
                    />
                    <button type="button" onClick={() => affiliateRefs[i].current?.click()}
                      className="px-2.5 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-xs transition-colors flex-shrink-0">
                      <Upload size={12} />
                    </button>
                    <input ref={affiliateRefs[i]} type="file" accept="image/*" className="hidden" onChange={uploadAffiliateLogo(i)} />
                  </div>
                  <input
                    value={logo.link_url || ""}
                    onChange={(e) => updateAffiliate(i, "link_url", e.target.value)}
                    placeholder="Partner-URL (https://...)"
                    data-testid={`affiliate-link-url-${i}`}
                    className={`${inputCls} py-2 text-xs`}
                  />
                  <input
                    value={logo.title || ""}
                    onChange={(e) => updateAffiliate(i, "title", e.target.value)}
                    placeholder="Bezeichnung (z.B. Webhosting Partner)"
                    className={`${inputCls} py-2 text-xs`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Tracking & Scripts */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-4">
            <div>
              <h2 className="text-white font-bold text-sm">Tracking &amp; Scripts</h2>
              <p className="text-zinc-500 text-xs mt-0.5">Nur Developer können diese Felder sehen und bearbeiten.</p>
            </div>

            {/* Google Analytics */}
            <div>
              <label className={labelCls}>Google Analytics – Measurement-ID</label>
              <input
                value={form.google_analytics_id}
                onChange={set("google_analytics_id")}
                className={inputCls}
                placeholder="G-XXXXXXXXXX"
                data-testid="settings-ga-id"
              />
              <p className="text-zinc-600 text-xs mt-1">
                Trage deine GA4-Measurement-ID ein. Das Tracking-Script wird automatisch in den &lt;head&gt; eingebunden.
              </p>
            </div>

            {/* Cookie Banner */}
            <div>
              <label className={labelCls}>Cookie-Banner Code (CCM-Script)</label>
              <textarea
                value={form.cookie_banner_code}
                onChange={set("cookie_banner_code")}
                rows={7}
                className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2.5 text-xs font-mono leading-relaxed focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600 resize-y min-h-[140px]"
                placeholder="<!-- Füge hier deinen Cookie-Banner Script-Code ein -->"
                data-testid="settings-cookie-banner"
              />
              <p className="text-zinc-600 text-xs mt-1">
                HTML/Script-Code wird direkt in den &lt;head&gt; injiziert (z.B. Consentmanager, Cookiebot).
              </p>
            </div>
          </div>

          <button type="submit" disabled={saving} data-testid="save-settings-btn"
            className="flex items-center gap-2 px-6 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold rounded-lg transition-colors">
            <Save size={16} /> {saving ? "Speichern..." : "Einstellungen speichern"}
          </button>
        </form>
      </div>
      </div>
    </AdminLayout>
  );
}
